// PR-12.3/12.4 — Admin team CRUD + multi-invite UI.
// Ref: audit-reports/league-system/01-current-state-and-roadmap.md §6 row 3-4
//
// TASK-66 PR-D: captain concept dropped entirely. The previous "Force-swap
// captain" mutation + Crown / "Lagkapten" UI rows are removed. Both players
// render as equal in TeamCard with kick controls on each. CreateTeamDialog
// now picks "Spelare 1" (no captain wording). The createTeam mutation
// auto-mirrors player1 into teams.captain_id / captain_customer_id for
// backward-compat until PR-E drops those columns.
//
// TASK-67 PR-D: player1/2 columns are gone (PR-E drops them). The
// CreateTeamDialog now collects primaries + reserves as multi-selects
// directly, capped by leagues.max_primary_per_team /
// leagues.max_reserves_per_team (PR-A added these columns). createTeam
// inserts ONLY the teams row (no player1/2 columns) plus one
// team_members row per selected primary/reserve. TeamCard headers also
// surface the per-league caps as "({count}/{max})".
//
// PR-12.3 actions (after TASK-66 PR-D):
//   - Kick player (delete team_members row) — equality: any player kickable
//   - Reassign division (UPDATE teams.division_id)
//
// PR-12.4 additions:
//   - "Bjud in fler" dialog per team — any team member or admin can send N
//     invitations in one batch via team_invitations table. Each invite gets
//     a magic-link copyable to clipboard. Accept flow lives in JoinTeam.tsx
//     via the accept_team_invitation() RPC (race-condition safe).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2,
  Users,
  AlertTriangle,
  UserMinus,
  UserPlus,
  Mail,
  Copy,
  Check,
  X,
  Plus,
  Trash2,
  // TASK-67 PR-D Feature 2: inline edit affordance for team_name on TeamCard.
  Pencil,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  splitRoster,
  validateRosterSize,
  getTeamMemberDisplay,
  type TeamMemberRow,
  type TeamMemberRole,
} from "@/lib/teamHelpers";
import { parseEmailList } from "@/lib/teamInviteHelpers";
import SearchablePlayerSelector from "./SearchablePlayerSelector";

// TASK-67 PR-D: LeagueLite picks up max_primary_per_team +
// max_reserves_per_team (PR-A added those columns). The CreateTeamDialog
// + AddPlayerDialog use these to cap multi-select picker sizes; TeamCard
// labels also surface them as "({count}/{max})".
type LeagueLite = {
  id: string;
  name: string;
  min_players_per_team: number;
  max_players_per_team: number;
  max_primary_per_team: number;
  max_reserves_per_team: number;
};

// TASK-67 PR-D: local form state for the create-team dialog. Replaces
// the removed `CreateTeamFormState` shape — the player1* fields are
// gone (no separate "Spelare 1" picker), and the dialog collects
// primaries + reserves as multi-selects directly. The createTeam
// mutation inserts ONLY the teams row plus one team_members row per
// selected entry; player1/2 columns are dropped in PR-E.
interface CreateTeamForm {
  leagueId: string | null;
  divisionId: string | null;
  teamName: string;
  primaryUserIds: string[];
  primaryCustomerIds: string[];
  reserveUserIds: string[];
  reserveCustomerIds: string[];
}

function emptyCreateTeamForm(): CreateTeamForm {
  return {
    leagueId: null,
    divisionId: null,
    teamName: "",
    primaryUserIds: [],
    primaryCustomerIds: [],
    reserveUserIds: [],
    reserveCustomerIds: [],
  };
}

/**
 * TASK-67 PR-D: validate the create-team form against the league's
 * caps. Returns null on valid; a Swedish error string otherwise.
 *
 * Rules:
 *   - leagueId is required
 *   - at least 1 primary (registered or unregistered) — the team
 *     needs someone playing
 *   - primaries ≤ max_primary_per_team
 *   - reserves ≤ max_reserves_per_team
 *   - playing roster (primaries) within [min_players_per_team,
 *     max_players_per_team] for the league
 *   - no duplicate user_id or customer_id across primaries + reserves
 */
function validateCreateTeamForm(
  state: CreateTeamForm,
  league: LeagueLite | null,
): string | null {
  if (!state.leagueId) return "Välj serie";
  if (!league) return "Kunde inte ladda serieinfo";
  const primaryCount =
    state.primaryUserIds.length + state.primaryCustomerIds.length;
  const reserveCount =
    state.reserveUserIds.length + state.reserveCustomerIds.length;
  if (primaryCount < 1) return "Välj minst en ordinarie spelare";
  if (primaryCount > league.max_primary_per_team) {
    return `För många ordinarie spelare: ${primaryCount} av högst ${league.max_primary_per_team}`;
  }
  if (reserveCount > league.max_reserves_per_team) {
    return `För många reserver: ${reserveCount} av högst ${league.max_reserves_per_team}`;
  }
  if (primaryCount < league.min_players_per_team) {
    return `För få spelare: ${primaryCount} av minst ${league.min_players_per_team}`;
  }
  if (primaryCount > league.max_players_per_team) {
    return `För många spelare: ${primaryCount} av högst ${league.max_players_per_team}`;
  }
  // Dedup: a single user_id (or customer_id) can't appear twice across
  // primaries + reserves. The team_members UNIQUE(team_id, user_id) +
  // UNIQUE(team_id, customer_id) constraints would also reject this
  // server-side, but catching at form time keeps the UX cleaner.
  const seenUsers = new Set<string>();
  for (const id of [...state.primaryUserIds, ...state.reserveUserIds]) {
    if (seenUsers.has(id)) return "Samma spelare valts två gånger";
    seenUsers.add(id);
  }
  const seenCustomers = new Set<string>();
  for (const id of [...state.primaryCustomerIds, ...state.reserveCustomerIds]) {
    if (seenCustomers.has(id)) return "Samma spelare valts två gånger";
    seenCustomers.add(id);
  }
  return null;
}

type DivisionLite = { id: string; name: string; league_id: string };

type ProfileLite = {
  id: string;
  full_name: string | null;
  // UPPDRAG-23: customers (1-to-many in theory, but in practice 0-or-1
  // per profile per org because profiles.id is the user_id and the
  // RLS-scoped customers query naturally filters to the current org).
  // UPPDRAG-27: extended with full_name + email so PlayerNameButton can
  // fall back when profile.full_name is empty/null. Required because
  // many demo + matchi-imported profiles ship with full_name = "" while
  // the customer row carries the real name.
  customers: { id: string; full_name: string | null; email: string | null }[];
};

// UPPDRAG-60: TeamMemberRow now allows nullable user_id + new customer_id;
// for unregistered members the parent attaches a `customer` resolved from
// the customers table (separate query because there's no FK chain from
// team_members.customer_id → embedded fields with the existing PostgREST
// query shape).
type CustomerLiteForMember = { id: string; full_name: string | null; email: string | null };

type TeamWithRoster = Tables<"teams"> & {
  league: LeagueLite | null;
  division: DivisionLite | null;
  members: (TeamMemberRow & {
    profile: ProfileLite | null;
    customer: CustomerLiteForMember | null;
  })[];
};

export default function AdminTeams() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const qc = useQueryClient();
  const [leagueFilter, setLeagueFilter] = useState<string>("all");
  const [confirmKick, setConfirmKick] = useState<{
    teamId: string;
    memberId: string;
    userName: string;
  } | null>(null);
  // UPPDRAG-61: confirm-delete-team dialog state. Carries the team's
  // display name so the confirm body can show it without a re-fetch.
  const [confirmDeleteTeam, setConfirmDeleteTeam] = useState<{
    teamId: string;
    teamName: string;
  } | null>(null);
  // PR-12.4: invite dialog state (open per teamId)
  const [inviteForTeamId, setInviteForTeamId] = useState<string | null>(null);
  // UPPDRAG-23: create-team dialog state
  const [createOpen, setCreateOpen] = useState(false);
  // UPPDRAG-27: AddPlayerDialog state — full team object so the dialog can
  // read the league's max_players_per_team + the existing roster for
  // exclude/validation without a second query.
  const [addPlayerForTeam, setAddPlayerForTeam] = useState<TeamWithRoster | null>(null);

  const orgId = organization?.id;
  const navigate = useNavigate();

  // ── All teams in org with roster + league + division + customer-id-per-member ──
  //
  // BUGFIX (2026-04-28): UPPDRAG-23 originally embedded
  //   `customers(id, organization_id)` inside the
  //   `profile:profiles!team_members_user_id_fkey(...)` embed to resolve
  //   the customer-id-per-member in one round-trip. Problem: there's NO
  //   FK relationship from `profiles` → `customers` (verified via
  //   information_schema + supabase generated types — `profiles.Relationships
  //   = []`). PostgREST silently 400s the entire query when it can't
  //   resolve a relationship for an embed, returning `data: undefined`
  //   which destructures to `teams = []`. Net effect: AdminTeams showed
  //   "0 lag" / "Inga lag att visa" for every org, regardless of division
  //   state. Was masked while admins happened to have teams visible
  //   elsewhere.
  //
  // Fix: separate-queries pattern (mirrors AdminDivisions). Two round-trips
  // but reliable + scoped:
  //   1. Teams + league + division + members + profile via embed
  //      (well-formed FK paths only)
  //   2. Customers in current org WHERE user_id IN (member user_ids)
  //   3. Manual join via Map<user_id, customer_id>
  //
  // Side-effect: null-division teams now appear correctly because the
  // `division:divisions(...)` LEFT-JOIN works as designed (was masked by
  // the silent 400 above).
  const { data: teams = [], isLoading } = useQuery({
    queryKey: ["admin-teams", orgId],
    queryFn: async (): Promise<TeamWithRoster[]> => {
      // 1. Main query — strip the customers embed; keep only well-formed FK paths.
      //    UPPDRAG-60: select customer_id too (nullable post-migration).
      const { data, error } = await supabase
        .from("teams")
        // TASK-66 PR-D: dropped captain_id from SELECT. captain UI is gone;
        // PR-E drops the column.
        // TASK-67 PR-D: dropped player1_id, player2_id,
        // player2_invite_email, player2_invite_name from SELECT (all
        // PR-D-owned drops; player1/2 columns drop in PR-E). Roster is
        // sourced exclusively from the team_members embed; the league
        // embed now also surfaces max_primary_per_team +
        // max_reserves_per_team (PR-A added) so TeamCard can render the
        // per-section caps as "({count}/{max})".
        .select("id, league_id, division_id, team_name, status, payment_status, created_at, registration_date, invite_token, invite_sent_at, league:leagues!inner(id, name, organization_id, min_players_per_team, max_players_per_team, max_primary_per_team, max_reserves_per_team), division:divisions(id, name, league_id), members:team_members(id, team_id, user_id, customer_id, role, joined_at, profile:profiles!team_members_user_id_fkey(id, full_name))")
        .eq("league.organization_id", orgId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const teamsRaw = data ?? [];

      // 2a. Collect every member's user_id (registered) + customer_id
      //     (unregistered, UPPDRAG-60). Org-scoped lookups, manual joins.
      const memberUserIds = Array.from(
        new Set(
          teamsRaw.flatMap((tm) => (tm.members ?? [])
            .map((m) => (m as { user_id: string | null }).user_id)
            .filter((id): id is string => id !== null)),
        ),
      );
      const memberCustomerIds = Array.from(
        new Set(
          teamsRaw.flatMap((tm) => (tm.members ?? [])
            .map((m) => (m as { customer_id: string | null }).customer_id)
            .filter((id): id is string => id !== null)),
        ),
      );

      // 2b. Load customer rows by user_id (registered fallback chain).
      const customerByUser = new Map<string, CustomerLiteForMember>();
      if (memberUserIds.length > 0 && orgId) {
        const { data: customers } = await supabase
          .from("customers")
          .select("id, user_id, full_name, email")
          .eq("organization_id", orgId)
          .in("user_id", memberUserIds);
        for (const c of customers ?? []) {
          if (c.user_id) {
            customerByUser.set(c.user_id, {
              id: c.id,
              full_name: c.full_name,
              email: c.email ?? null,
            });
          }
        }
      }

      // 2c. UPPDRAG-60: load customer rows by customer_id (unregistered
      //     team_members rows). Same shape; keyed by customer.id.
      const customerById = new Map<string, CustomerLiteForMember>();
      if (memberCustomerIds.length > 0 && orgId) {
        const { data: customers } = await supabase
          .from("customers")
          .select("id, full_name, email")
          .eq("organization_id", orgId)
          .in("id", memberCustomerIds);
        for (const c of customers ?? []) {
          customerById.set(c.id, {
            id: c.id,
            full_name: c.full_name,
            email: c.email ?? null,
          });
        }
      }

      // 3. Augment each member with both the registered (profile.customers)
      //    chain AND the unregistered (customer) shortcut. getTeamMemberDisplay
      //    resolves a single name from whichever path applies.
      return teamsRaw.map((tm) => ({
        ...tm,
        members: (tm.members ?? []).map((m) => {
          const mm = m as TeamMemberRow & { user_id: string | null; customer_id: string | null; profile: ProfileLite | null };
          const customer = mm.user_id ? customerByUser.get(mm.user_id) ?? null : null;
          const customerDirect = mm.customer_id ? customerById.get(mm.customer_id) ?? null : null;
          return {
            ...mm,
            profile: mm.profile
              ? { ...mm.profile, customers: customer ? [customer] : [] }
              : null,
            customer: customerDirect,
          };
        }),
      })) as unknown as TeamWithRoster[];
    },
    enabled: !!orgId,
  });

  // ── Available leagues for filter dropdown ──
  const leagues = Array.from(
    new Map(
      teams
        .map((t) => t.league)
        .filter((l): l is LeagueLite => l !== null)
        .map((l) => [l.id, l]),
    ).values(),
  );

  const filteredTeams =
    leagueFilter === "all"
      ? teams
      : teams.filter((tm) => tm.league?.id === leagueFilter);

  // TASK-66 PR-D: swapCaptain mutation REMOVED. Captain concept dropped —
  // both players are equal in TeamCard now. Team_members rows still carry
  // role='captain' until PR-E's enum collapse, but that's invisible to the
  // UI (splitRoster merges captain into primaries).

  // ── UPPDRAG-23: create team (admin) ──
  // Sequential INSERT teams + INSERT team_members with manual rollback on
  // failure. An RPC would be cleaner for atomicity (cf. PR-12.4's
  // accept_team_invitation pattern) but admin traffic is low enough that
  // the rollback approach is acceptable; promote to RPC if usage warrants.
  //
  // UPPDRAG-25 (Bug 3): divisionId is OPTIONAL — admin "puzzles" division
  // placement after seeing all signed-up teams. Pass null to teams.division_id
  // (column is nullable per MCP schema check). Reassign dropdown on each card
  // can later set a real division.
  const createTeam = useMutation({
    mutationFn: async (form: CreateTeamForm) => {
      // TASK-67 PR-D: dropped player1/2_id + player1/2_customer_id writes
      // entirely (PR-E drops the columns). The dialog now collects
      // primaries + reserves directly as multi-selects; the form-level
      // validateCreateTeamForm enforces ≥1 primary + cap-respect. Defensive
      // belt-and-braces below mirrors the form-level rule.
      const primaryCount =
        form.primaryUserIds.length + form.primaryCustomerIds.length;
      if (!form.leagueId || primaryCount < 1) {
        throw new Error("Validering misslyckades");
      }

      // TASK-67 PR-D Feature 1: auto-generated team_name when user leaves
      // the "Lagnamn" field empty at create time. Resolve primary players'
      // full_names from profiles (registered) and customers (unregistered),
      // join with "/", store as the literal team_name. NOT recomputed on
      // render — the user can edit it later via the TeamCard pencil-edit
      // affordance (see Feature 2). Falls back to null if all primaries
      // resolved to empty (defensive — validation requires ≥1 primary).
      let computedTeamName = form.teamName.trim();
      if (!computedTeamName) {
        const profileFetch = form.primaryUserIds.length > 0
          ? supabase.from("public_profiles" as any).select("id, full_name").in("id", form.primaryUserIds)
          : Promise.resolve({ data: [] as { id: string; full_name: string | null }[] });
        const customerFetch = form.primaryCustomerIds.length > 0
          ? supabase.from("customers").select("id, full_name").in("id", form.primaryCustomerIds)
          : Promise.resolve({ data: [] as { id: string; full_name: string | null }[] });
        const [{ data: profileRows }, { data: customerRows }] = await Promise.all([
          profileFetch,
          customerFetch,
        ]);
        const profileMap = new Map(
          (profileRows ?? []).map((p) => [p.id, p.full_name?.trim() ?? ""]),
        );
        const customerMap = new Map(
          (customerRows ?? []).map((c) => [c.id, c.full_name?.trim() ?? ""]),
        );
        // Preserve user-selection order: registered primaries first, then
        // unregistered customer-primaries — matches the UX of "ordinarie
        // spelare" picker order.
        const names: string[] = [];
        for (const uid of form.primaryUserIds) {
          const n = profileMap.get(uid);
          if (n) names.push(n);
        }
        for (const cid of form.primaryCustomerIds) {
          const n = customerMap.get(cid);
          if (n) names.push(n);
        }
        computedTeamName = names.join("/");
      }

      // INSERT teams. NO player1/2 columns — only the league + division +
      // optional team name + payment + status. UPPDRAG-25: division_id
      // may be null (optional at creation time).
      type TeamInsert = {
        league_id: string;
        division_id: string | null;
        team_name: string | null;
        payment_status: string;
        status: string;
      };
      const teamInsert: TeamInsert = {
        league_id: form.leagueId,
        division_id: form.divisionId,
        team_name: computedTeamName || null,
        payment_status: "pending",
        status: "active",
      };
      const { data: createdTeam, error: teamErr } = await supabase
        .from("teams")
        .insert(teamInsert as never)
        .select("id")
        .single();
      if (teamErr) throw teamErr;
      const teamId = createdTeam.id;

      // INSERT team_members — one row per selected primary/reserve. Each
      // row carries EITHER user_id (registered) OR customer_id (UPPDRAG-60:
      // unregistered customer); the team_members_identity_check CHECK
      // constraint enforces that exactly one of the two is set. Wrap in
      // try/catch so we can roll back the team on failure (no real
      // transaction without RPC).
      try {
        const memberRows: Array<{
          team_id: string;
          user_id?: string;
          customer_id?: string;
          role: TeamMemberRole;
        }> = [
          ...form.primaryUserIds.map((uid) => ({
            team_id: teamId,
            user_id: uid,
            role: "primary" as TeamMemberRole,
          })),
          ...form.reserveUserIds.map((uid) => ({
            team_id: teamId,
            user_id: uid,
            role: "reserve" as TeamMemberRole,
          })),
          // UPPDRAG-60: unregistered primaries + reserves carry customer_id.
          ...form.primaryCustomerIds.map((cid) => ({
            team_id: teamId,
            customer_id: cid,
            role: "primary" as TeamMemberRole,
          })),
          ...form.reserveCustomerIds.map((cid) => ({
            team_id: teamId,
            customer_id: cid,
            role: "reserve" as TeamMemberRole,
          })),
        ];
        const { error: memErr } = await supabase
          .from("team_members")
          .insert(memberRows as never);
        if (memErr) throw memErr;
      } catch (innerErr) {
        // Rollback: delete the orphan team
        await supabase.from("teams").delete().eq("id", teamId);
        throw innerErr;
      }
      // UPPDRAG-25 (Bug 1+2): return total roster count so onSuccess can
      // craft a clear toast with player info AND decide whether to
      // auto-open the InviteDialog (skip if admin already added initial
      // players — the auto-open was confusing in that case).
      // TASK-67 PR-D: count = primaries + reserves (no separate player1).
      return {
        teamId,
        addedPlayerCount:
          form.primaryUserIds.length + form.reserveUserIds.length
          + form.primaryCustomerIds.length + form.reserveCustomerIds.length,
      };
    },
    onSuccess: ({ teamId, addedPlayerCount }) => {
      qc.invalidateQueries({ queryKey: ["admin-teams"] });
      setCreateOpen(false);
      // UPPDRAG-25 (Bug 1): explicit player-count in the success toast so
      // admin sees clear confirmation that the team was saved (was
      // previously a brief generic "Lag skapat" that could be missed when
      // the InviteDialog opened on top of it).
      // UPPDRAG-25 (Bug 2): only auto-open InviteDialog when admin did
      // NOT add any initial players. If they already populated startspelare
      // via SearchablePlayerSelector, asking for invite emails is
      // redundant — show a toast with a "Bjud in fler" action button so
      // they can opt-in if desired.
      // TASK-67 PR-D: dialog now requires ≥1 primary so addedPlayerCount
      // is always ≥1. Show "InviteDialog auto-open" only when admin added
      // exactly 1 player (the minimum) — the typical "I want to invite my
      // partner via email" flow.
      const hasInitialRoster = addedPlayerCount > 1;

      if (hasInitialRoster) {
        toast.success(
          t("teams.teamCreatedWithPlayers", "Lag skapat med {{n}} spelare", {
            n: addedPlayerCount,
          }),
          {
            duration: 6000,
            action: {
              label: t("teams.inviteMore", "Bjud in fler"),
              onClick: () => setInviteForTeamId(teamId),
            },
          },
        );
      } else {
        toast.success(t("teams.teamCreatedOpenInvite", "Lag skapat — bjud in spelare"), {
          duration: 4000,
        });
        // No initial players → auto-open InviteDialog so admin can invite
        // teammates without an extra click. Matches the original PR-12.4 flow.
        setInviteForTeamId(teamId);
      }
    },
    onError: (err: Error) =>
      toast.error(err.message || "Kunde inte skapa lag"),
  });

  // ── PR-12.4: send invitations (multi-batch) ──
  const sendInvitations = useMutation({
    mutationFn: async ({
      teamId,
      emails,
      intendedRole,
    }: {
      teamId: string;
      emails: string[];
      intendedRole: TeamMemberRole;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Ej autentiserad");
      // One row per email — magic-link token per invitation so any team
      // member or admin can copy multiple links at once. crypto.randomUUID()
      // for collision resistance + UNIQUE constraint as belt-and-braces.
      const rows = emails.map((email) => ({
        team_id: teamId,
        invited_by_user_id: user.id,
        invited_email: email,
        invite_token: crypto.randomUUID(),
        intended_role: intendedRole,
      }));
      const { data, error } = await supabase
        .from("team_invitations")
        .insert(rows)
        .select("id, invite_token, invited_email");
      if (error) throw error;
      return data ?? [];
    },
    onSuccess: (created) => {
      toast.success(
        t("teams.invitationsSent", "{{n}} inbjudningar skapade", {
          n: created.length,
        }),
      );
      qc.invalidateQueries({ queryKey: ["admin-teams"] });
      qc.invalidateQueries({ queryKey: ["team-invitations"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Kick player (delete team_members row) ──
  const kickPlayer = useMutation({
    mutationFn: async ({ memberId }: { memberId: string }) => {
      const { error } = await supabase
        .from("team_members")
        .delete()
        .eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("teams.playerKicked", "Spelare borttagen"));
      qc.invalidateQueries({ queryKey: ["admin-teams"] });
      setConfirmKick(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── UPPDRAG-61: Delete team via SECURITY DEFINER RPC ──
  // The RPC enforces admin-role authz + blocks deletes when the team has
  // played matches (status IN completed/disputed/walkover) or any
  // walkover-penalty rows. On block it RAISEs with a message starting
  // with the code "team_has_played_matches" — we sniff for that prefix
  // in onError to swap the toast text. CASCADE handles team_members,
  // team_invitations, team_reserves, standings, etc. (per FK survey in
  // the investigation report).
  const deleteTeam = useMutation({
    mutationFn: async ({ teamId }: { teamId: string }) => {
      const { error } = await supabase.rpc(
        "delete_team" as never,
        { p_team_id: teamId } as never,
      );
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("teams.deleteSuccess", "Lag raderat"));
      qc.invalidateQueries({ queryKey: ["admin-teams"] });
      setConfirmDeleteTeam(null);
    },
    onError: (err: Error) => {
      // The RPC raises with EXCEPTION 'team_has_played_matches: ...';
      // PostgREST surfaces that as err.message. Sniff for the code prefix
      // and swap to the user-friendly Swedish toast (no "arkivera"
      // suggestion per Q4 — there's no archive flow today).
      const msg = err.message ?? "";
      if (msg.includes("team_has_played_matches")) {
        toast.error(
          t(
            "teams.deleteBlocked",
            "Kan inte radera lag som har spelat matcher eller walkover-historik.",
          ),
        );
      } else {
        toast.error(msg || t("teams.deleteFailed", "Kunde inte radera lag"));
      }
    },
  });

  // ── UPPDRAG-27: Add player directly (no email invite) ──
  // Complements PR-12.4's InviteDialog: when admin already knows the
  // player exists in this org's customer base, skip the email invite
  // round-trip and INSERT the team_members row directly. Same RLS
  // policy as the team-member flow (TASK-66 PR-B-v2's "Team members
  // can manage team_members" + "Admin can manage team_members") so
  // admins + team members can both use this path.
  // UPPDRAG-60: addPlayer now accepts either userId (registered) or
  // customerId (unregistered). The team_members_identity_check CHECK
  // requires exactly one to be non-null.
  const addPlayer = useMutation({
    mutationFn: async ({
      teamId,
      userId,
      customerId,
      role,
    }: {
      teamId: string;
      userId: string | null;
      customerId: string | null;
      role: TeamMemberRole;
    }) => {
      if (!userId && !customerId) {
        throw new Error("Antingen registrerad spelare eller kund krävs");
      }
      const payload: { team_id: string; role: TeamMemberRole; user_id?: string; customer_id?: string } = {
        team_id: teamId,
        role,
      };
      if (userId) payload.user_id = userId;
      if (customerId) payload.customer_id = customerId;
      const { error } = await supabase
        .from("team_members")
        .insert(payload as never);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("teams.playerAdded", "Spelare tillagd"));
      qc.invalidateQueries({ queryKey: ["admin-teams"] });
      setAddPlayerForTeam(null);
    },
    onError: (err: Error) =>
      toast.error(err.message || t("teams.couldNotAddPlayer", "Kunde inte lägga till spelare")),
  });

  // ── Reassign division ──
  const reassignDivision = useMutation({
    mutationFn: async ({
      teamId,
      divisionId,
    }: {
      teamId: string;
      divisionId: string | null;
    }) => {
      const { error } = await supabase
        .from("teams")
        .update({ division_id: divisionId })
        .eq("id", teamId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("teams.divisionReassigned", "Division ändrad"));
      qc.invalidateQueries({ queryKey: ["admin-teams"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar: filter + create button (UPPDRAG-23) */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-muted-foreground">
          {t("teams.filterByLeague", "Filtrera på serie:")}
        </span>
        <Select value={leagueFilter} onValueChange={setLeagueFilter}>
          <SelectTrigger className="w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all", "Alla")}</SelectItem>
            {leagues.map((l) => (
              <SelectItem key={l.id} value={l.id}>
                {l.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {filteredTeams.length} {t("teams.teamsCount", "lag")}
        </span>
        <Button
          size="sm"
          onClick={() => setCreateOpen(true)}
          className="ml-auto gap-1"
        >
          <Plus className="h-4 w-4" />
          {t("teams.createCta", "Skapa lag")}
        </Button>
      </div>

      {filteredTeams.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
            {t("teams.noTeams", "Inga lag att visa")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredTeams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              onKickPlayer={(memberId, userName) =>
                setConfirmKick({ teamId: team.id, memberId, userName })
              }
              onReassignDivision={(divisionId) =>
                reassignDivision.mutate({ teamId: team.id, divisionId })
              }
              onOpenInvite={() => setInviteForTeamId(team.id)}
              onOpenAddPlayer={() => setAddPlayerForTeam(team)}
              onPlayerClick={(customerId, userName) => {
                if (customerId) {
                  navigate(`/manager/customers/${customerId}`);
                } else {
                  toast.info(
                    t(
                      "teams.noCustomerForUser",
                      "{{name}} har ingen kund-koppling i denna org",
                      { name: userName },
                    ),
                  );
                }
              }}
              // UPPDRAG-61: hand a callback that pops the confirm dialog
              // with the team's display name (parent owns the dialog state
              // — same pattern as confirmKick).
              onDeleteTeam={() =>
                setConfirmDeleteTeam({
                  teamId: team.id,
                  teamName: team.team_name || t("teams.unnamedTeam", "Namnlöst lag"),
                })
              }
              reassignPending={reassignDivision.isPending}
            />
          ))}
        </div>
      )}

      {/* Confirm kick dialog */}
      <Dialog open={!!confirmKick} onOpenChange={() => setConfirmKick(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("teams.confirmKickTitle", "Ta bort spelare?")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "teams.confirmKickBody",
                "Spelaren tas bort från laget direkt. Hen kan bjudas in igen senare av lagkaptenen.",
              )}{" "}
              <strong>{confirmKick?.userName}</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmKick(null)}>
              {t("common.cancel", "Avbryt")}
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                confirmKick && kickPlayer.mutate({ memberId: confirmKick.memberId })
              }
              disabled={kickPlayer.isPending}
            >
              {kickPlayer.isPending && (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              )}
              {t("teams.kickConfirmAction", "Ta bort")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UPPDRAG-61: Confirm delete-team dialog. Mirrors the kick-player
          dialog shape (Dialog + destructive confirm + secondary cancel)
          for consistency with the existing AdminTeams pattern. The
          mutation's onError sniffs for the RPC's "team_has_played_matches"
          message prefix and swaps the toast accordingly. */}
      <Dialog
        open={!!confirmDeleteTeam}
        onOpenChange={(open) => !open && setConfirmDeleteTeam(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("teams.deleteTeam", "Radera lag")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "teams.deleteConfirm",
                "Är du säker på att du vill radera laget '{{teamName}}'? Detta kan inte ångras.",
                { teamName: confirmDeleteTeam?.teamName ?? "" },
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteTeam(null)}
              disabled={deleteTeam.isPending}
            >
              {t("common.cancel", "Avbryt")}
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                confirmDeleteTeam &&
                deleteTeam.mutate({ teamId: confirmDeleteTeam.teamId })
              }
              disabled={deleteTeam.isPending}
            >
              {deleteTeam.isPending && (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              )}
              <Trash2 className="h-4 w-4 mr-1" />
              {t("teams.deleteTeam", "Radera lag")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PR-12.4: Multi-invite dialog */}
      <InviteDialog
        teamId={inviteForTeamId}
        onClose={() => setInviteForTeamId(null)}
        onSubmit={(emails, intendedRole) => {
          if (!inviteForTeamId) return;
          sendInvitations.mutate(
            { teamId: inviteForTeamId, emails, intendedRole },
            { onSuccess: () => setInviteForTeamId(null) },
          );
        }}
        isPending={sendInvitations.isPending}
      />

      {/* UPPDRAG-23: Create-team dialog */}
      <CreateTeamDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(form) => createTeam.mutate(form)}
        isPending={createTeam.isPending}
      />

      {/* UPPDRAG-27: Direct-add player dialog
          UPPDRAG-60: now accepts either userId (registered) or customerId
          (unregistered). The dialog hands back exactly one of them. */}
      {orgId && (
        <AddPlayerDialog
          team={addPlayerForTeam}
          organizationId={orgId}
          onClose={() => setAddPlayerForTeam(null)}
          onSubmit={(selection, role) => {
            if (!addPlayerForTeam) return;
            addPlayer.mutate({
              teamId: addPlayerForTeam.id,
              userId: selection.userId,
              customerId: selection.customerId,
              role,
            });
          }}
          isPending={addPlayer.isPending}
        />
      )}
    </div>
  );
}

// ─── InviteDialog (PR-12.4) ────────────────────────────────────────────

function InviteDialog({
  teamId,
  onClose,
  onSubmit,
  isPending,
}: {
  teamId: string | null;
  onClose: () => void;
  onSubmit: (emails: string[], intendedRole: TeamMemberRole) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation("admin");
  const [emailsText, setEmailsText] = useState("");
  const [intendedRole, setIntendedRole] = useState<TeamMemberRole>("primary");

  const emails = parseEmailList(emailsText);
  const valid = emails.length > 0;

  const handleSubmit = () => {
    if (!valid) return;
    onSubmit(emails, intendedRole);
    setEmailsText("");
    setIntendedRole("primary");
  };

  return (
    <Dialog open={!!teamId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("teams.inviteDialogTitle", "Bjud in spelare")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "teams.inviteDialogBody",
              "En inbjudningslänk skickas per e-post. Spelare som accepterar läggs till i laget. Bjud in flera samtidigt — först-till-kvarn-principen om laget blir fullt.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="invite-emails">
              {t("teams.inviteEmailsLabel", "E-postadresser (en per rad)")}
            </Label>
            <Textarea
              id="invite-emails"
              placeholder="anna@example.com&#10;bjorn@example.com"
              value={emailsText}
              onChange={(e) => setEmailsText(e.target.value)}
              rows={4}
            />
            {emailsText.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {emails.length}{" "}
                {t("teams.validEmailsCount", "giltiga e-postadresser")}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>{t("teams.inviteRoleLabel", "Roll vid acceptans")}</Label>
            <Select
              value={intendedRole}
              onValueChange={(v) => setIntendedRole(v as TeamMemberRole)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="primary">
                  {t("teams.rolePrimary", "Spelare")}
                </SelectItem>
                <SelectItem value="reserve">
                  {t("teams.roleReserve", "Reserv")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel", "Avbryt")}
          </Button>
          <Button onClick={handleSubmit} disabled={!valid || isPending}>
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            <Mail className="h-4 w-4 mr-1" />
            {t("teams.sendInvitations", "Skicka inbjudningar")}
            {emails.length > 0 && ` (${emails.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// parseEmailList moved to src/lib/teamInviteHelpers.ts (importable by
// vitest without pulling in the supabase client). See that file.

// ─── AddPlayerDialog (UPPDRAG-27) ──────────────────────────────────────
// Direct-add dialog: pick an existing player via SearchablePlayerSelector
// (UPPDRAG-24), choose a role, INSERT into team_members. No email
// invitation — admin must already know the player exists in this org's
// customer base. Use InviteDialog instead when you need to invite a
// stranger.
//
// Roster-size validation: blocks submit when adding a primary would push
// the playing roster over `league.max_players_per_team`. Reserves are
// excluded from the count (matches `validateRosterSize` semantics in
// teamHelpers.ts).
//
// `team` is null when the dialog is closed; the parent passes the full
// TeamWithRoster object when opening so this component can read both
// the league config (for max-players) and the existing member list (for
// excludeUserIds + roster math) without a second query.

function AddPlayerDialog({
  team,
  organizationId,
  onClose,
  onSubmit,
  isPending,
}: {
  team: TeamWithRoster | null;
  organizationId: string;
  onClose: () => void;
  // UPPDRAG-60: selection now carries either userId or customerId; AdminTeams
  // routes to the correct team_members column based on which is set.
  onSubmit: (
    selection: { userId: string | null; customerId: string | null; name: string; isRegistered: boolean },
    role: TeamMemberRole,
  ) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation("admin");
  const [selected, setSelected] = useState<
    { userId: string | null; customerId: string | null; name: string; isRegistered: boolean } | null
  >(null);
  const [role, setRole] = useState<TeamMemberRole>("primary");

  if (!team) return null;

  // UPPDRAG-60: collect both registered + unregistered IDs to exclude.
  const existingUserIds = team.members
    .map((m) => m.user_id)
    .filter((id): id is string => id !== null);
  const existingCustomerIds = team.members
    .map((m) => m.customer_id)
    .filter((id): id is string => id !== null);
  // TASK-66 PR-E: enum dropped 'captain' value; only 'primary' counts
  // toward playing roster (was 'captain || primary' for pre-PR-E rows).
  // TASK-67 PR-D: cap-checking now uses the per-league
  // max_primary_per_team / max_reserves_per_team instead of the legacy
  // single max_players_per_team. The shared helper ensures the dialog
  // can't push past the role-specific cap.
  const primaryCount = team.members.filter((m) => m.role === "primary").length;
  const reserveCount = team.members.filter((m) => m.role === "reserve").length;
  const wouldExceedMax =
    !!team.league &&
    (role === "primary"
      ? primaryCount + 1 > team.league.max_primary_per_team
      : reserveCount + 1 > team.league.max_reserves_per_team);

  const handleClose = () => {
    setSelected(null);
    setRole("primary");
    onClose();
  };

  const handleSubmit = () => {
    if (!selected || wouldExceedMax) return;
    onSubmit(selected, role);
    setSelected(null);
    setRole("primary");
  };

  const submitDisabled = !selected || wouldExceedMax || isPending;

  return (
    <Dialog open={!!team} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("teams.addPlayerDialogTitle", "Lägg till spelare")}
            {team.team_name ? ` — ${team.team_name}` : ""}
          </DialogTitle>
          <DialogDescription>
            {t(
              "teams.addPlayerDialogBody",
              "Sök en befintlig spelare i organisationen och lägg till direkt — ingen e-postinbjudan skickas. Använd \"Bjud in fler\" om spelaren inte finns i kundregistret än.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Player picker */}
          <div className="space-y-1">
            <Label>{t("teams.addPlayerSearchLabel", "Spelare")}</Label>
            {selected ? (
              <div className="flex items-center justify-between text-sm bg-accent/50 rounded px-3 py-2">
                <span className="font-medium flex items-center gap-2">
                  {selected.name}
                  {!selected.isRegistered && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1 font-normal">
                      {t("teams.notRegistered", "Ej registrerad")}
                    </Badge>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  aria-label={t("teams.addPlayerClear", "Rensa val")}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              // UPPDRAG-60: requireRegistered=false → shows BOTH registered
              // and unregistered customers; the badge surfaces which is which.
              <SearchablePlayerSelector
                organizationId={organizationId}
                excludeUserIds={existingUserIds}
                excludeCustomerIds={existingCustomerIds}
                placeholder={t("teams.addPlayerSearchPlaceholder", "Sök spelare (namn eller e-post)")}
                onSelect={(p) =>
                  setSelected({
                    userId: p.user_id,
                    customerId: p.isRegistered ? null : p.customer_id,
                    name: p.full_name,
                    isRegistered: p.isRegistered,
                  })
                }
              />
            )}
          </div>

          {/* Role picker */}
          <div className="space-y-1">
            <Label>{t("teams.addPlayerRoleLabel", "Roll")}</Label>
            <Select value={role} onValueChange={(v) => setRole(v as TeamMemberRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="primary">
                  {t("teams.rolePrimary", "Spelare")}
                </SelectItem>
                <SelectItem value="reserve">
                  {t("teams.roleReserve", "Reserv")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* TASK-67 PR-D: roster-full warning is now role-aware. Triggers
              when adding the picked role would exceed the league's per-role
              cap (max_primary_per_team or max_reserves_per_team). */}
          {wouldExceedMax && team.league && (
            <p className="text-xs text-destructive bg-destructive/10 px-2 py-1 rounded">
              {role === "primary"
                ? t(
                    "teams.addPlayerPrimaryFull",
                    "Ordinarie-platserna är fulla ({{count}}/{{max}}). Lägg till som reserv eller ta bort en ordinarie först.",
                    {
                      count: primaryCount,
                      max: team.league.max_primary_per_team,
                    },
                  )
                : t(
                    "teams.addPlayerReserveFull",
                    "Reserv-platserna är fulla ({{count}}/{{max}}). Ta bort en reserv först.",
                    {
                      count: reserveCount,
                      max: team.league.max_reserves_per_team,
                    },
                  )}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("common.cancel", "Avbryt")}
          </Button>
          <Button onClick={handleSubmit} disabled={submitDisabled}>
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            <UserPlus className="h-4 w-4 mr-1" />
            {t("teams.addPlayerSubmit", "Lägg till")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── TeamCard ──────────────────────────────────────────────────────────

function TeamCard({
  team,
  onKickPlayer,
  onReassignDivision,
  onOpenInvite,
  onOpenAddPlayer,
  onPlayerClick,
  // UPPDRAG-61: pop the confirm-delete dialog at parent level.
  onDeleteTeam,
  reassignPending,
}: {
  team: TeamWithRoster;
  onKickPlayer: (memberId: string, userName: string) => void;
  onReassignDivision: (divisionId: string | null) => void;
  onOpenInvite: () => void;
  /** UPPDRAG-27: open the AddPlayerDialog for this team (direct-add path). */
  onOpenAddPlayer: () => void;
  /** UPPDRAG-23: navigate to /manager/customers/:customerId. customerId can
   * be null when no customer row exists for this user_id in the current org
   * (e.g. invited-but-not-yet-customer-imported). */
  onPlayerClick: (customerId: string | null, userName: string) => void;
  /** UPPDRAG-61: open the confirm-delete-team dialog. */
  onDeleteTeam: () => void;
  reassignPending: boolean;
}) {
  const { t } = useTranslation("admin");
  const qc = useQueryClient();
  // TASK-66 PR-D: splitRoster no longer returns a captain slot — the
  // 'captain' role rows merge into primaries so the captain user appears
  // alongside other players with the same kick controls.
  const { primaries, reserves } = splitRoster(team.members);
  const rosterError = team.league
    ? validateRosterSize(team.members, team.league)
    : null;

  // TASK-67 PR-D Feature 2: inline edit affordance for team_name. Pencil
  // icon next to the title opens an inline input + save/cancel. RLS already
  // allows admin (Admin all teams policy) and team_members (PR-B-v2's
  // is_team_member()) to UPDATE — same trust model as the rest of the
  // roster controls. No auto-name regeneration on add/remove member —
  // explicit edit only, per Q from Rickard.
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(team.team_name ?? "");
  const saveTeamNameMutation = useMutation({
    mutationFn: async (newName: string) => {
      const trimmed = newName.trim();
      // Empty input → clear the override and let downstream renderers
      // fall back to "Namnlöst lag" placeholder. Non-empty → store
      // verbatim (capped at 100 chars to match the column convention).
      const valueToStore = trimmed.length === 0 ? null : trimmed.slice(0, 100);
      const { error } = await supabase
        .from("teams")
        .update({ team_name: valueToStore })
        .eq("id", team.id);
      if (error) throw error;
      return valueToStore;
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["admin-teams"] });
      setEditingName(false);
      setDraftName(saved ?? "");
      toast.success(t("teams.teamNameSaved", "Lagnamn sparat"));
    },
    onError: (err: Error) => {
      toast.error(err?.message || t("teams.teamNameSaveError", "Kunde inte spara lagnamn"));
    },
  });

  // UPPDRAG-25 (Bug 3 follow-up): load ALL divisions for the team's league
  // so admin can reassign a NULL-division team (or change between
  // divisions). The previous implementation only listed the team's
  // CURRENT division as an option (see prior TODO at this line) — which
  // meant a team with no division had only "none" available, breaking
  // the "create-without-division then assign later" flow.
  const { data: leagueDivisions = [] } = useQuery({
    queryKey: ["team-card-divisions", team.league_id],
    queryFn: async () => {
      if (!team.league_id) return [];
      const { data, error } = await supabase
        .from("divisions")
        .select("id, name, level")
        .eq("league_id", team.league_id)
        .order("level", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!team.league_id,
  });
  // PR-12.4: invite button is only useful when there's room for more players.
  // TASK-67 PR-D: cap-check uses the new per-role caps. Invite is allowed
  // as long as either primaries OR reserves have a free slot.
  const primaryCountForCard = primaries.length;
  const reserveCountForCard = reserves.length;
  const canInvite = team.league
    ? primaryCountForCard < team.league.max_primary_per_team
      || reserveCountForCard < team.league.max_reserves_per_team
    : true;

  // TASK-66 PR-D: removed `eligibleNewCaptains` derivation — captain
  // concept dropped, no swap UI to feed.

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              {/* TASK-67 PR-D Feature 2: inline-edit team_name with pencil
                  affordance. View mode shows the name + small ghost-pencil
                  next to it. Edit mode swaps to an Input + save/cancel
                  buttons, sized to fit inline. Enter saves; Escape cancels. */}
              {editingName ? (
                <span className="flex items-center gap-1.5 flex-1 min-w-0">
                  <Input
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        saveTeamNameMutation.mutate(draftName);
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        setEditingName(false);
                        setDraftName(team.team_name ?? "");
                      }
                    }}
                    autoFocus
                    maxLength={100}
                    placeholder={t("teams.unnamedTeam", "Namnlöst lag")}
                    className="h-7 text-sm flex-1 min-w-0"
                    disabled={saveTeamNameMutation.isPending}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={() => saveTeamNameMutation.mutate(draftName)}
                    disabled={saveTeamNameMutation.isPending}
                    aria-label={t("teams.saveTeamName", "Spara lagnamn")}
                  >
                    {saveTeamNameMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Check className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={() => {
                      setEditingName(false);
                      setDraftName(team.team_name ?? "");
                    }}
                    disabled={saveTeamNameMutation.isPending}
                    aria-label={t("teams.cancelEdit", "Avbryt")}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </span>
              ) : (
                <>
                  <span className="truncate">
                    {team.team_name || t("teams.unnamedTeam", "Namnlöst lag")}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setDraftName(team.team_name ?? "");
                      setEditingName(true);
                    }}
                    aria-label={t("teams.editTeamName", "Redigera lagnamn")}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Badge variant={team.status === "active" ? "default" : "secondary"} className="text-xs">
                    {team.status}
                  </Badge>
                </>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {team.league?.name ?? "—"}
              {team.division && ` · ${team.division.name}`}
            </p>
          </div>
          {rosterError && (
            <div className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/30 px-2 py-1 rounded">
              <AlertTriangle className="h-3.5 w-3.5" />
              {rosterError}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {/* TASK-66 PR-D: Captain row + Crown icon + "Byt lagkapten" Select
            REMOVED. The captain user now appears in the Primaries section
            below with the same kick controls as everyone else. */}

        {/* Primaries — TASK-67 PR-D: header reflects the league's
            max_primary_per_team cap so admins see headroom at a glance. */}
        {primaries.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">
              {t("teams.primaries", "Ordinarie")} ({primaries.length}
              {team.league ? `/${team.league.max_primary_per_team}` : ""})
            </p>
            <ul className="space-y-1">
              {primaries.map((m) => {
                // UPPDRAG-60: dual-identity resolver picks the right fallback chain.
                const display = getTeamMemberDisplay(m, t("teams.unknownPlayer", "Okänd spelare"));
                return (
                  <li key={m.id} className="flex items-center justify-between text-sm bg-muted/30 rounded px-2 py-1">
                    <span className="flex items-center gap-2">
                      <PlayerNameButton member={m} onPlayerClick={onPlayerClick} />
                      {!display.isRegistered && (
                        <Badge variant="outline" className="text-[10px] h-4 px-1 font-normal">
                          {t("teams.notRegistered", "Ej registrerad")}
                        </Badge>
                      )}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() => onKickPlayer(m.id, display.name)}
                    >
                      <UserMinus className="h-3.5 w-3.5 mr-1" />
                      {t("teams.kick", "Ta bort")}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Reserves — TASK-67 PR-D: header reflects the league's
            max_reserves_per_team cap. */}
        {reserves.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">
              {t("teams.reserves", "Reserver")} ({reserves.length}
              {team.league ? `/${team.league.max_reserves_per_team}` : ""})
            </p>
            <ul className="space-y-1">
              {reserves.map((m) => {
                const display = getTeamMemberDisplay(m, t("teams.unknownPlayer", "Okänd spelare"));
                return (
                  <li key={m.id} className="flex items-center justify-between text-sm bg-muted/20 rounded px-2 py-1">
                    <span className="flex items-center gap-2">
                      <PlayerNameButton member={m} onPlayerClick={onPlayerClick} muted />
                      {!display.isRegistered && (
                        <Badge variant="outline" className="text-[10px] h-4 px-1 font-normal">
                          {t("teams.notRegistered", "Ej registrerad")}
                        </Badge>
                      )}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() => onKickPlayer(m.id, display.name)}
                    >
                      <UserMinus className="h-3.5 w-3.5 mr-1" />
                      {t("teams.kick", "Ta bort")}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Division reassign */}
        <div className="flex items-center gap-2 text-sm pt-2 border-t">
          <span className="text-xs text-muted-foreground">
            {t("teams.division", "Division")}:
          </span>
          <Select
            value={team.division_id ?? "none"}
            onValueChange={(v) => onReassignDivision(v === "none" ? null : v)}
            disabled={reassignPending}
          >
            <SelectTrigger className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                {t("teams.noDivision", "Ingen division")}
              </SelectItem>
              {/* UPPDRAG-25 (Bug 3 follow-up): list ALL divisions for this
                  team's league so admin can move between divisions or
                  promote a NULL-division team to a real division. */}
              {leagueDivisions.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* UPPDRAG-35: defense-in-depth hint when the team's league has
            zero divisions. Steady-state should never trigger this thanks
            to the trg_leagues_create_default_division trigger + backfill,
            but a manual deletion or future bug could leave a league
            empty. The hint points admins to the Serier-redigering ->
            Divisions tab where they can create more. */}
        {leagueDivisions.length === 0 && (
          <div className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded px-2 py-1 border border-amber-200 dark:border-amber-900">
            {t(
              "teams.noDivisionsHint",
              "Den här serien har inga divisioner. Skapa minst en via Serier → redigera → Divisioner.",
            )}
          </div>
        )}

        <div className="flex items-center gap-2 text-sm pt-1">

          {/* UPPDRAG-27: direct-add button — disabled when roster is full
              (uses same `canInvite` predicate since both paths add a new
              team_members row that counts toward max_players_per_team).
              Sits BEFORE "Bjud in fler" so the typical workflow (admin
              knows the player) is the first action; email-invite is the
              fallback when admin doesn't know the user. */}
          <Button
            size="sm"
            variant="outline"
            className="ml-auto h-7 text-xs gap-1"
            onClick={onOpenAddPlayer}
            disabled={!canInvite}
            title={
              canInvite
                ? undefined
                : t("teams.rosterFullTooltip", "Laget är fullt")
            }
          >
            <UserPlus className="h-3.5 w-3.5" />
            {t("teams.addPlayerCta", "Lägg till spelare")}
          </Button>

          {/* PR-12.4: invite button — disabled when roster is full */}
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1"
            onClick={onOpenInvite}
            disabled={!canInvite}
            title={
              canInvite
                ? undefined
                : t("teams.rosterFullTooltip", "Laget är fullt")
            }
          >
            <Mail className="h-3.5 w-3.5" />
            {t("teams.inviteCta", "Bjud in fler")}
          </Button>

          {/* UPPDRAG-61: destructive ghost button — opens the confirm-
              delete-team dialog at parent level. text-destructive +
              hover:text-destructive matches the existing destructive-
              ghost pattern (cf. PendingInvitationsPanel revoke button
              + LeagueRoundsManager delete-round button). The actual RPC
              also blocks server-side when the team has played matches
              or walkover-history, so the worst this can do is fail
              cleanly with a toast. */}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={onDeleteTeam}
            title={t("teams.deleteTeamTooltip", "Radera laget från serien")}
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t("teams.deleteTeam", "Radera lag")}
          </Button>
        </div>

        {/* PR-12.4: pending invitations panel (collapsed by default) */}
        <PendingInvitationsPanel teamId={team.id} />
      </CardContent>
    </Card>
  );
}

// ─── PendingInvitationsPanel (PR-12.4) ─────────────────────────────────

function PendingInvitationsPanel({ teamId }: { teamId: string }) {
  const { t } = useTranslation("admin");
  const qc = useQueryClient();
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const { data: invites = [] } = useQuery({
    queryKey: ["team-invitations", teamId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_invitations")
        .select("id, invited_email, invite_token, intended_role, expires_at, status, created_at")
        .eq("team_id", teamId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from("team_invitations")
        .update({ status: "revoked", revoked_at: new Date().toISOString() })
        .eq("id", invitationId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("teams.invitationRevoked", "Inbjudan återkallad"));
      qc.invalidateQueries({ queryKey: ["team-invitations", teamId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (invites.length === 0) return null;

  const copyLink = (token: string) => {
    const url = `${window.location.origin}/join-team/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    });
  };

  return (
    <div className="space-y-1 pt-2 border-t">
      <p className="text-xs font-medium text-muted-foreground">
        {t("teams.pendingInvitations", "Väntande inbjudningar")} ({invites.length})
      </p>
      <ul className="space-y-1">
        {invites.map((inv) => (
          <li
            key={inv.id}
            className="flex items-center justify-between text-xs bg-muted/20 rounded px-2 py-1 gap-2"
          >
            <span className="truncate flex-1">
              {inv.invited_email ?? "—"}{" "}
              <Badge variant="outline" className="text-[10px] ml-1">
                {inv.intended_role}
              </Badge>
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => copyLink(inv.invite_token)}
              >
                {copiedToken === inv.invite_token ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-destructive"
                onClick={() => revokeMutation.mutate(inv.id)}
                disabled={revokeMutation.isPending}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── PlayerNameButton (UPPDRAG-23, fallback chain UPPDRAG-27) ──────────
// Renders a player's name as a clickable element. Resolves the user_id →
// customer_id lookup via the embedded `profile.customers` array (filtered
// to current org in the parent query). If no customer row exists in this
// org, the click shows an info toast instead of navigating.
//
// UPPDRAG-27 (Bug 2): profile.full_name often empty-string ("") for
// demo + matchi-imported users — the previous `?? user_id.slice(0,8)`
// only caught NULL/undefined and rendered "" (invisible button).
// MCP confirmed: in Padel Demoklubben, all 15 team_members have
// profiles.full_name = "" while customers.full_name has the real name
// ("Erik Andersson", "Sofia Björk", ...) and customers.email holds
// "demo+customerNNN@boply.app". Fallback chain:
//   1. profile.full_name (if truthy + non-empty)
//   2. customer.full_name (often present when profile name is missing)
//   3. customer.email
//   4. "Okänd spelare" (translated)
//
// TASK-66 PR-D: removed `resolvePlayerName` helper — its only callsite
// was the captain-Select option labels in the now-deleted Captain row UI.
// `PlayerNameButton` below uses `getTeamMemberDisplay` directly, which
// handles both registered (profile + embedded customer) and unregistered
// (top-level customer) paths via the same fallback chain.

function PlayerNameButton({
  member,
  onPlayerClick,
  muted = false,
}: {
  // UPPDRAG-60: member shape now allows nullable user_id + customer_id
  // (unregistered) and a top-level `customer` field for the unregistered
  // path. getTeamMemberDisplay resolves either side.
  member: TeamMemberRow & {
    user_id: string | null;
    customer_id: string | null;
    profile: {
      id: string;
      full_name: string | null;
      customers: { id: string; full_name: string | null; email: string | null }[];
    } | null;
    customer: { id: string; full_name: string | null; email: string | null } | null;
  };
  onPlayerClick: (customerId: string | null, userName: string) => void;
  muted?: boolean;
}) {
  const { t } = useTranslation("admin");
  const display = getTeamMemberDisplay(member, t("teams.unknownPlayer", "Okänd spelare"));
  return (
    <button
      type="button"
      onClick={() => onPlayerClick(display.customerId, display.name)}
      className={
        muted
          ? "text-muted-foreground hover:text-foreground hover:underline transition-colors text-left"
          : "hover:underline hover:text-primary transition-colors text-left"
      }
    >
      {display.name}
    </button>
  );
}

// ─── CreateTeamDialog (UPPDRAG-23) ─────────────────────────────────────
// Admin form to create a new team in one shot. League → division →
// primaries (≥1 required, ≤max_primary_per_team) → reserves (optional,
// ≤max_reserves_per_team). Validates inline via validateCreateTeamForm
// against the picked league's caps.
//
// TASK-66 PR-D: captain wording removed.
// TASK-67 PR-D: dialog no longer has a separate "Spelare 1" slot. The
// required first member is just "the first primary" — the multi-select
// pattern is the one canonical source for both primaries + reserves.
// The createTeam mutation INSERTs ONLY the teams row (no player1/2
// columns) plus one team_members row per selected entry.

function CreateTeamDialog({
  open,
  onClose,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (form: CreateTeamForm) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const orgId = organization?.id;
  const [form, setForm] = useState<CreateTeamForm>(emptyCreateTeamForm());
  // UPPDRAG-24: name lookup. Populated as the searchable selector
  // returns players. Used to render selected primaries/reserves chips
  // without a second round-trip to the DB.
  const [playerNames, setPlayerNames] = useState<Map<string, string>>(new Map());

  // ── Leagues in org (for the league selector) ──
  // UPPDRAG-35 (Bug B): added 'in_progress' to the status filter so admins
  // can register late teams to leagues that have already started. Earlier
  // the filter excluded in_progress leagues entirely → Vårserien Liga 2026
  // (status=in_progress) didn't appear in the dropdown, making the Skapa-
  // lag dialog look broken (no leagues to pick from). 'completed' is still
  // intentionally excluded — historical leagues stay locked.
  // TASK-67 PR-D: SELECT now includes max_primary_per_team +
  // max_reserves_per_team so the dialog can cap each multi-select.
  const { data: leagues = [] } = useQuery<LeagueLite[]>({
    queryKey: ["create-team-leagues", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leagues")
        .select("id, name, min_players_per_team, max_players_per_team, max_primary_per_team, max_reserves_per_team")
        .eq("organization_id", orgId!)
        .in("status", ["draft", "registration_open", "active", "in_progress"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as LeagueLite[];
    },
    enabled: open && !!orgId,
  });

  // ── Divisions for the selected league ──
  const { data: divisions = [] } = useQuery({
    queryKey: ["create-team-divisions", form.leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("divisions")
        .select("id, name, league_id")
        .eq("league_id", form.leagueId!)
        .order("level", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: open && !!form.leagueId,
  });

  // UPPDRAG-24: dropped the bulk customers query (it loaded ALL org
  // customers into memory). Now the SearchablePlayerSelector below issues
  // org-scoped, debounced ilike queries on demand. Scales to 5000+ customers
  // (whereas the bulk dropdown choked at ~200).

  const selectedLeague = leagues.find((l) => l.id === form.leagueId) ?? null;
  const validationError = validateCreateTeamForm(form, selectedLeague);
  const submitDisabled = !!validationError || isPending;

  // Reset form on close
  const handleClose = () => {
    setForm(emptyCreateTeamForm());
    setPlayerNames(new Map());
    onClose();
  };

  // UPPDRAG-24: register a player's name in the lookup map so chips
  // render without re-fetch.
  const rememberName = (userId: string, fullName: string) => {
    setPlayerNames((prev) => {
      if (prev.get(userId) === fullName) return prev;
      const next = new Map(prev);
      next.set(userId, fullName);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("teams.createDialogTitle", "Skapa lag")}</DialogTitle>
          <DialogDescription>
            {t(
              "teams.createDialogBody",
              "Skapa ett nytt lag och tilldela spelare 1 + valfria startspelare. Du kan bjuda in fler senare via inbjudningar.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* League */}
          <div className="space-y-1">
            <Label>{t("teams.createLeague", "Serie")}</Label>
            <Select
              value={form.leagueId ?? ""}
              onValueChange={(v) =>
                setForm((f) => ({
                  ...f,
                  leagueId: v,
                  divisionId: null, // reset division when league changes
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t("teams.createLeaguePlaceholder", "Välj serie")} />
              </SelectTrigger>
              <SelectContent>
                {leagues.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name} ({l.min_players_per_team}-{l.max_players_per_team} spelare)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Division — UPPDRAG-25 (Bug 3): now optional */}
          <div className="space-y-1">
            <Label>
              {t("teams.createDivision", "Division")}{" "}
              <span className="text-xs text-muted-foreground font-normal">
                {t("teams.createDivisionOptionalHint", "(valfritt — kan tilldelas senare)")}
              </span>
            </Label>
            <Select
              // UPPDRAG-25: Radix Select disallows empty-string SelectItem
              // values. Use a sentinel "__none__" for "no division" and map
              // it to null on selection so the form stores the DB shape directly.
              value={form.divisionId ?? "__none__"}
              onValueChange={(v) =>
                setForm((f) => ({ ...f, divisionId: v === "__none__" ? null : v }))
              }
              disabled={!form.leagueId}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    form.leagueId
                      ? t("teams.createDivisionPlaceholder", "Välj division (valfritt)")
                      : t("teams.createSelectLeagueFirst", "Välj serie först")
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  {t("teams.createDivisionNone", "Tilldelas senare")}
                </SelectItem>
                {divisions.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Team name */}
          <div className="space-y-1">
            <Label>{t("teams.createTeamName", "Lagnamn (valfritt)")}</Label>
            <Input
              value={form.teamName}
              onChange={(e) => setForm((f) => ({ ...f, teamName: e.target.value }))}
              placeholder="ex. Pegasus"
            />
          </div>

          {/* TASK-67 PR-D: dropped the standalone "Spelare 1" picker.
              Primaries + reserves are collected as multi-selects; the
              first primary is the equivalent of the legacy player1 slot.
              Each role has its own per-league cap (max_primary_per_team /
              max_reserves_per_team from PR-A).

              Both pickers accept either registered users (→ *UserIds) or
              unregistered customers (→ *CustomerIds). Each entry maps 1:1
              to a team_members row; the team_members_identity_check
              constraint guarantees exactly-one-identity per row.

              Add-button is disabled at the per-role cap; chips have a
              "Ta bort" X that drops the entry from the appropriate
              array. */}
          {(() => {
            const primaryCount =
              form.primaryUserIds.length + form.primaryCustomerIds.length;
            const reserveCount =
              form.reserveUserIds.length + form.reserveCustomerIds.length;
            const maxPrimary = selectedLeague?.max_primary_per_team ?? 0;
            const maxReserves = selectedLeague?.max_reserves_per_team ?? 0;
            return (
              <>
                {/* Ordinarie spelare — required ≥1, capped by max_primary_per_team */}
                <div className="space-y-1">
                  <Label>
                    {t("teams.createPrimaries", "Ordinarie spelare")} (
                    {primaryCount}/{maxPrimary})
                  </Label>
                  {orgId && primaryCount < maxPrimary && (
                    <SearchablePlayerSelector
                      organizationId={orgId}
                      placeholder={t(
                        "teams.createAddPrimary",
                        "Lägg till ordinarie",
                      )}
                      excludeUserIds={[
                        ...form.primaryUserIds,
                        ...form.reserveUserIds,
                      ]}
                      excludeCustomerIds={[
                        ...form.primaryCustomerIds,
                        ...form.reserveCustomerIds,
                      ]}
                      onSelect={(p) => {
                        if (p.isRegistered && p.user_id) {
                          rememberName(p.user_id, p.full_name);
                          setForm((f) => ({
                            ...f,
                            primaryUserIds: [
                              ...f.primaryUserIds,
                              p.user_id as string,
                            ],
                          }));
                        } else {
                          rememberName(p.customer_id, p.full_name);
                          setForm((f) => ({
                            ...f,
                            primaryCustomerIds: [
                              ...f.primaryCustomerIds,
                              p.customer_id,
                            ],
                          }));
                        }
                      }}
                    />
                  )}
                  {(form.primaryUserIds.length > 0
                    || form.primaryCustomerIds.length > 0) && (
                    <ul className="mt-1 space-y-1">
                      {form.primaryUserIds.map((uid) => (
                        <li
                          key={`pu:${uid}`}
                          className="flex items-center justify-between text-xs bg-muted/30 rounded px-2 py-1"
                        >
                          <span>{playerNames.get(uid) ?? uid.slice(0, 8)}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                primaryUserIds: f.primaryUserIds.filter(
                                  (x) => x !== uid,
                                ),
                              }))
                            }
                            aria-label={t("teams.removePrimary", "Ta bort spelare")}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                      {form.primaryCustomerIds.map((cid) => (
                        <li
                          key={`pc:${cid}`}
                          className="flex items-center justify-between text-xs bg-muted/30 rounded px-2 py-1"
                        >
                          <span className="flex items-center gap-2">
                            {playerNames.get(cid) ?? cid.slice(0, 8)}
                            <Badge
                              variant="outline"
                              className="text-[10px] h-4 px-1 font-normal"
                            >
                              {t("teams.notRegistered", "Ej registrerad")}
                            </Badge>
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                primaryCustomerIds: f.primaryCustomerIds.filter(
                                  (x) => x !== cid,
                                ),
                              }))
                            }
                            aria-label={t("teams.removePrimary", "Ta bort spelare")}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Reserver — optional, capped by max_reserves_per_team */}
                <div className="space-y-1">
                  <Label>
                    {t("teams.createReserves", "Reserver")} ({reserveCount}/
                    {maxReserves})
                  </Label>
                  {orgId && reserveCount < maxReserves && (
                    <SearchablePlayerSelector
                      organizationId={orgId}
                      placeholder={t(
                        "teams.createAddReserve",
                        "Lägg till reserv",
                      )}
                      excludeUserIds={[
                        ...form.primaryUserIds,
                        ...form.reserveUserIds,
                      ]}
                      excludeCustomerIds={[
                        ...form.primaryCustomerIds,
                        ...form.reserveCustomerIds,
                      ]}
                      onSelect={(p) => {
                        if (p.isRegistered && p.user_id) {
                          rememberName(p.user_id, p.full_name);
                          setForm((f) => ({
                            ...f,
                            reserveUserIds: [
                              ...f.reserveUserIds,
                              p.user_id as string,
                            ],
                          }));
                        } else {
                          rememberName(p.customer_id, p.full_name);
                          setForm((f) => ({
                            ...f,
                            reserveCustomerIds: [
                              ...f.reserveCustomerIds,
                              p.customer_id,
                            ],
                          }));
                        }
                      }}
                    />
                  )}
                  {(form.reserveUserIds.length > 0
                    || form.reserveCustomerIds.length > 0) && (
                    <ul className="mt-1 space-y-1">
                      {form.reserveUserIds.map((uid) => (
                        <li
                          key={`ru:${uid}`}
                          className="flex items-center justify-between text-xs bg-muted/20 rounded px-2 py-1"
                        >
                          <span className="text-muted-foreground">
                            {playerNames.get(uid) ?? uid.slice(0, 8)}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                reserveUserIds: f.reserveUserIds.filter(
                                  (x) => x !== uid,
                                ),
                              }))
                            }
                            aria-label={t("teams.removeReserve", "Ta bort reserv")}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                      {form.reserveCustomerIds.map((cid) => (
                        <li
                          key={`rc:${cid}`}
                          className="flex items-center justify-between text-xs bg-muted/20 rounded px-2 py-1"
                        >
                          <span className="text-muted-foreground flex items-center gap-2">
                            {playerNames.get(cid) ?? cid.slice(0, 8)}
                            <Badge
                              variant="outline"
                              className="text-[10px] h-4 px-1 font-normal"
                            >
                              {t("teams.notRegistered", "Ej registrerad")}
                            </Badge>
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                reserveCustomerIds: f.reserveCustomerIds.filter(
                                  (x) => x !== cid,
                                ),
                              }))
                            }
                            aria-label={t("teams.removeReserve", "Ta bort reserv")}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            );
          })()}

          {validationError && (
            <p className="text-xs text-destructive bg-destructive/10 px-2 py-1 rounded">
              {validationError}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("common.cancel", "Avbryt")}
          </Button>
          <Button
            onClick={() => onSubmit(form)}
            disabled={submitDisabled}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            <Plus className="h-4 w-4 mr-1" />
            {t("teams.createSubmit", "Skapa lag")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
